import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { pageKeys } from '@/shared/query/queryKeys';

/**
 * Route-driven page selection.
 *
 * Resolves which page should be open (explicit id, or the most recent page
 * when none is given), stores only its id, and seeds the detail cache so
 * useCurrentPage renders without a second fetch.
 */
export function usePageSelection() {
  const queryClient = useQueryClient();
  const setStoreCurrentPageId = usePageStore(state => state.setCurrentPageId);

  const setCurrentPageId = useCallback(
    async (id?: string | null) => {
      if (id === null) {
        setStoreCurrentPageId(null);
        return null;
      }

      if (id) {
        const page = await queryClient.fetchQuery({
          queryKey: pageKeys.detail(id),
          // Query data must not be undefined; a missing page is null
          queryFn: async () => (await pageRepository.getPageById(id)) ?? null,
        });
        if (page) {
          setStoreCurrentPageId(id);
          return id;
        }

        setStoreCurrentPageId(null);
        return null;
      }

      // No id given: open the most recently updated page.
      const firstPage = await queryClient.fetchQuery({
        queryKey: pageKeys.first('updatedAt'),
        queryFn: async () => (await pageRepository.getFirstPage('updatedAt')) ?? null,
      });
      if (firstPage) {
        queryClient.setQueryData(pageKeys.detail(firstPage.id), firstPage);
        setStoreCurrentPageId(firstPage.id);
        return firstPage.id;
      }

      // Root list is empty; fall back to any page (e.g. one inside a collection).
      const pages = await pageRepository.getAllPages();
      if (pages.length) {
        queryClient.setQueryData(pageKeys.detail(pages[0].id), pages[0]);
        setStoreCurrentPageId(pages[0].id);
        return pages[0].id;
      }

      return null;
    },
    [queryClient, setStoreCurrentPageId]
  );

  return {
    setCurrentPageId,
  };
}
